import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectBrainId } from '@/redux/session/selectors';
import { actions as permissionActions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

import { useUpdateImprovement } from '../hooks/useUpdateImprovement';
import type { Improvement, ReviewAction } from '../types';
import { REVIEW_ACTION_STATUS } from '../types';
import { availableActions } from '../utils';

/** Orchestrator caps review feedback at 512 characters. */
const MAX_FEEDBACK_LENGTH = 512;

/** Per-action UI config; the submitted status lives in REVIEW_ACTION_STATUS (D3). */
const REVIEW_ACTIONS = {
  apply: {
    labelKey: 'improvements.apply',
    titleKey: 'improvements.apply_confirm_title',
    descriptionKey: 'improvements.apply_confirm_description',
    variant: 'primary',
  },
  dismiss: {
    labelKey: 'improvements.dismiss',
    titleKey: 'improvements.dismiss_confirm_title',
    descriptionKey: 'improvements.dismiss_confirm_description',
    variant: 'destructive',
  },
  archive: {
    labelKey: 'improvements.archive',
    titleKey: 'improvements.archive_confirm_title',
    descriptionKey: 'improvements.archive_confirm_description',
    variant: 'outline-neutral',
  },
} as const satisfies Record<
  ReviewAction,
  { labelKey: string; titleKey: string; descriptionKey: string; variant: string }
>;

interface ImprovementActionsProps {
  improvement: Improvement;
  /** Called after the review action succeeds (e.g. to close the detail panel). */
  onDone?: () => void;
}

export const ImprovementActions = ({ improvement, onDone }: ImprovementActionsProps) => {
  const { t } = useTranslation();
  const brainId = useSelector(selectBrainId);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'brains', permissionActions.WRITE)
  );
  const { mutate: updateImprovement, isPending } = useUpdateImprovement(brainId);

  const [pendingAction, setPendingAction] = useState<ReviewAction | null>(null);
  const [feedback, setFeedback] = useState('');

  const actions = availableActions(improvement.status);
  if (actions.length === 0) {
    return null;
  }

  // Capturing the reason is required when rejecting, optional otherwise (D3).
  const isFeedbackRequired = pendingAction === 'dismiss';
  // Enforce the orchestrator's 512-char cap in code, not just via the textarea.
  const trimmedFeedback = feedback.trim().slice(0, MAX_FEEDBACK_LENGTH);

  const closeDialog = () => {
    setPendingAction(null);
    setFeedback('');
  };

  const handleConfirm = () => {
    if (!pendingAction) {
      return;
    }
    // Keep the dialog open (and feedback intact) until the mutation settles so a
    // failed PATCH doesn't discard what the user typed; close only on success.
    updateImprovement(
      {
        id: improvement.improvement_id,
        body: {
          status: REVIEW_ACTION_STATUS[pendingAction],
          ...(trimmedFeedback ? { feedback: trimmedFeedback } : {}),
        },
      },
      {
        onSuccess: () => {
          onDone?.();
          closeDialog();
        },
      }
    );
  };

  return (
    <>
      <div className="flex w-full flex-wrap gap-2">
        {actions.map((action) => (
          <Button
            key={action}
            size="sm"
            variant={REVIEW_ACTIONS[action].variant}
            disabled={isPending || !brainId || !canWrite}
            onClick={() => setPendingAction(action)}
          >
            {t(REVIEW_ACTIONS[action].labelKey)}
          </Button>
        ))}
      </div>

      <AlertDialog
        open={!!pendingAction}
        onOpenChange={(open) => !open && !isPending && closeDialog()}
      >
        <AlertDialogContent>
          {pendingAction && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>{t(REVIEW_ACTIONS[pendingAction].titleKey)}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t(REVIEW_ACTIONS[pendingAction].descriptionKey)}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="improvement-feedback">
                  {isFeedbackRequired
                    ? t('improvements.feedback_label')
                    : t('improvements.feedback_label_optional')}
                </Label>
                <Textarea
                  id="improvement-feedback"
                  value={feedback}
                  maxLength={MAX_FEEDBACK_LENGTH}
                  placeholder={t('improvements.feedback_placeholder')}
                  onChange={(e) => setFeedback(e.target.value)}
                />
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isPending}>{t('common.cancel')}</AlertDialogCancel>
                {/* Plain Button (not AlertDialogAction) so it doesn't auto-close;
                    the mutation's onSuccess closes the dialog once it settles. */}
                <Button
                  variant={REVIEW_ACTIONS[pendingAction].variant}
                  disabled={
                    isPending || !brainId || !canWrite || (isFeedbackRequired && !trimmedFeedback)
                  }
                  onClick={handleConfirm}
                >
                  {t(REVIEW_ACTIONS[pendingAction].labelKey)}
                </Button>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
